import { Checkbox } from '@/components/ui/checkbox';
import { useEffect, useMemo, useRef, useState } from 'react';

import { revealLeadContact } from '@/dashboard/api';
import { portalApiPost, portalApiGet, PortalApiError } from '@/lib/portal-api';
import type { TodayAppointment } from '@/dashboard/types';
// FRONT-OFFICE-OS-V2-BUILD-V1 (FO-5/FO-7 surface grammar): the wizard is
// WORK, so its host is the centered ModalShell -- the SAME props contract
// as the DrawerShell it replaces, every pinned control inside unchanged
// (the #414 mounted-content recomposition precedent: zero pin edits).
import {
  AcButton,
  ConfirmDialog,
  ModalShell,
} from '@/dashboard/ui';
import ConvertLeadToPatientControl from '@/dashboard/patients/ConvertLeadToPatientControl';
import LeadContactRevealControl, {
  type RevealOutcomePayload,
} from '@/dashboard/owner/LeadContactReveal';
import {
  ARRIVAL_CANCEL_DIALOG_DISCARD,
  ARRIVAL_CANCEL_DIALOG_KEEP,
  ARRIVAL_CANCEL_DIALOG_TITLE,
  ARRIVAL_CANCEL_REGISTRATION,
  ARRIVAL_EDIT_DETAILS,
  ARRIVAL_RESUMED_HEAD,
  ARRIVAL_RESUMED_SUB,
  ARRIVAL_SEARCH_UNAVAILABLE,
  ARRIVAL_STAGE_1_AUTHOR,
  ARRIVAL_STAGE_1_TITLE,
  ARRIVAL_STAGE_2_AUTHOR,
  ARRIVAL_STAGE_2_TITLE,
  ARRIVAL_STAGE_3_AUTHOR,
  ARRIVAL_STAGE_3_TITLE,
  ARRIVAL_DRAFT_VERSION,
  ARRIVAL_SUBMIT_LABEL,
  ARRIVAL_SUCCESS_HEADLINE,
  ARRIVAL_SUCCESS_ID_LABEL,
  arrivalCancelDialogBody,
  arrivalDraftKey,
  arrivalFormIsMinor,
  arrivalAddressDefaultsPatch,
  buildArrivalCheckpointBody,
  captureIncompleteLine,
  capturedSuccessLine,
  EMPTY_ARRIVAL_FORM,
  FALLBACK_REGISTRATION_CONFIG,
  parseArrivalDraft,
  registrationSectionsPrefill,
  resolvedRequiredFields,
  serializeArrivalDraft,
  stillNeededLine,
  validateArrivalConsent,
  validateArrivalDetails,
  validateArrivalReason,
  validateArrivalReturningConfirm,
  type ArrivalFieldError,
  type ArrivalPath,
  type ArrivalPickedPatient,
  type ArrivalStepId,
  type ArrivalWizardForm,
  type RegistrationConfigView,
} from '@/dashboard/patients/register-wizard-model';
import {
  RegisterConsentStep,
  RegisterDetailsFields,
  RegisterReasonStep,
  RegisterReturningConfirm,
  RegisterSearchStep,
  StillNeededLine,
  ThreeStageStrip,
  type SearchResultCardView,
} from '@/dashboard/patients/RegisterPatientSteps';
import {
  ConsentPhotoCaptureSlot,
  PENDING_PHOTO_LINE_SUCCESS,
  PendingPhotoBlock,
} from '@/dashboard/patients/consent-photo';
import type { PatientSearchResponse } from '@/dashboard/patients/patient-master-view';

/**
 * FRONTDESK-CHECKIN-AND-IDENTITY-V1 (Scope 1) -- THE CHECK-IN IDENTITY STEP,
 * hosting THE ARRIVAL REGISTRATION CHECKPOINT (UMR-AT-REGISTRATION-V1), now
 * COMPOSED AS THE ARRIVAL WIZARD (INTAKE-V2-BUILD-V1; authority
 * docs/design/intake-registration-v2/DESIGN-BRIEF.md #410 + founder rulings
 * D2-D7 2026-07-31). NO new panel, NO new nav row, NO new door -- V2 is a
 * RE-COMPOSITION of this host's interior (brief T10); the checkpoint wire,
 * the duplicate-first ladder, the candidacy fence, and the mint path are
 * BYTE-UNCHANGED server contracts.
 *
 * THE TWO HOPS THIS PANEL HOLDS (ruling 3 -- the QUEUE LAW superseded
 * NARROWLY at exactly one hop):
 *   - action 'arrived': UNCHANGED by V2. Physical presence is NEVER blocked;
 *     the light identity offer (the #244 bridge + the audited reveal) and
 *     the ONE surviving skip door stay exactly as shipped.
 *   - action 'checked_in': THE CHECKPOINT, presented as the SEARCH-FIRST
 *     step wizard (mockup 01): search -> details -> reason -> consent (NEW
 *     path) -> review -> success. The path resolves after the search step
 *     (RETURNING = 4 steps, NEW = 5, the minor stays 5) and never changes
 *     mid-flow. Submit still runs POST
 *     /api/appointments/:id/registration-checkpoint -- duplicate step FIRST,
 *     link-never-re-mint, UMR minted by the DB trigger on a confirmed
 *     no-match. The check-in skip door stays RETIRED (server-enforced 409).
 *
 * WIZARD LAWS CARRIED HERE:
 *   - SEARCH FIRST (C13): the only creation door renders after a search
 *     returned nothing suitable (or `None of these`); search itself uses
 *     THE existing patient-master search read. When that read is
 *     unavailable the step degrades HONESTLY -- the register door stays
 *     open because the server-side duplicate check still runs first at
 *     submit (the real fence, unchanged).
 *   - D3: per-step validation on Continue; the error renders beside the
 *     exact field, focus moves to it, nothing typed is lost; no whole-form
 *     locking; no per-keystroke server validation.
 *   - D5 (V1-SIMPLE): the draft lives on THIS device, keyed by appointment,
 *     feeds the ONE existing submit, and is never a second writer. Every
 *     inactive step panel stays MOUNTED (hidden) so typed values survive
 *     navigation within the session too.
 *   - C14: paper consent raises the loud PENDING PHOTO state with exactly
 *     one action; the submit then carries NO registrationPrivacyConsent --
 *     paper consent is not recorded until photographed.
 *
 * DUPLICATE-FIRST VERDICTS (server-derived; this panel only renders them):
 *   - deterministic auto-link -> linked in one motion (the ONLY auto-link).
 *   - possible_match / candidate_only -> candidate CARDS, NO default
 *     selection, staff picks explicitly (FAMILY-NEVER-AUTO-SELECTS); the
 *     explicit confirm is audited server-side.
 *   - reconciliation_pending -> the pick stays lawful but NO new record can
 *     be minted (a third UMR is refused; carrier PATIENT-MERGE-V1).
 * Every derived state is ZERO WRITES server-side; errors render VERBATIM.
 */

export const CHECKIN_IDENTITY_EYEBROW = 'Front-desk check-in';
export const CHECKIN_IDENTITY_INTRO =
  'Confirm who this is first - link or register the patient, then check in.';
export const CHECKIN_IDENTITY_GATE_DARK_COPY =
  'Patient records are not enabled for this organization yet - check-in continues without registration.';
export const CHECKIN_IDENTITY_LINKED_COPY =
  'Identity resolved - this visit now belongs to the named patient record.';
export const CHECKIN_IDENTITY_PHONE_HINT =
  'Confirm by phone (recorded in the audit log):';

/** The hop this panel is holding: the board's arrived or checked-in act. */
export type CheckinIdentityAction = 'arrived' | 'checked_in';

/** The proceed label per held hop -- mirrors the board's action copy. */
export const PROCEED_NOW_COPY: Readonly<Record<CheckinIdentityAction, string>> = {
  arrived: 'Mark arrived now',
  checked_in: 'Check in now',
};

/** THE ONE SURVIVING SKIP DOOR (UMR-AT-REGISTRATION-V1 ruling 3): arrival is
 *  one-tap and identity-free, so its skip door stays. The check-in skip door
 *  is RETIRED -- arrived -> checked_in requires the completed registration
 *  checkpoint (server-enforced), so no client door may pretend otherwise. */
export const PROCEED_SKIP_COPY: Readonly<Record<'arrived', string>> = {
  arrived: 'Mark arrived without registering',
};

export const NOT_NOW_COPY = 'Not now';

// --- Checkpoint copy (exported for pinning) --------------------------------

export const CHECKPOINT_TITLE = 'Registration checkpoint';
export const CHECKPOINT_INTRO =
  'Register or link the patient record - the duplicate check runs first.';
export const CHECKPOINT_SUBMIT_LABEL = ARRIVAL_SUBMIT_LABEL;
export const CHECKPOINT_LINK_CANDIDATE_LABEL = 'Link this patient';
export const CHECKPOINT_CREATE_ANYWAY_LABEL = 'None of these - register new';
export const CHECKPOINT_RECONCILIATION_COPY =
  'These records are pending duplicate reconciliation - link the correct patient or resolve the merge queue first. A new record will not be created.';
// The duplicate-review copy retired with its outcome -- LEAD-UNTIL-CONSULT
// ruling (DOCTOR-OS-EXPERIENCE-V1, 2026-08-04): the checkpoint no longer
// mints, so the writer-refused duplicate state cannot occur (no dead
// exports; the #246 lesson).
export const CHECKPOINT_BACK_EDIT_LABEL = 'Back - edit details';
export const CHECKPOINT_CONSENT_LABEL = 'Registration/privacy consent given';
export const CHECKPOINT_ALERT_LABEL = 'Critical alert -- handle with care';
export const CHECKPOINT_COMPLAINT_LABEL = 'Reason for visit (one line)';
export const CHECKPOINT_CLINIC_NOTE =
  'Registers at this appointment\'s clinic.';

/** The checkpoint client (injectable for tests). Mirrors the server result
 *  shape (api/routes/frontdesk-registration-checkpoint.ts).
 *
 *  LEAD-UNTIL-CONSULT ruling (DOCTOR-OS-EXPERIENCE-V1, 2026-08-04): MINT
 *  RETIRED at the checkpoint -- the mint success and the writer-refused
 *  duplicate outcomes are GONE from the vocabulary. Registration now
 *  captures ONTO THE LEAD ('registration_captured'; no patient, no UMR,
 *  no stamp); the writer-refused duplicate cannot occur because the
 *  checkpoint no longer mints. Every result carries leadId + the
 *  capture-completeness pair. */
export interface CheckpointCandidate {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string;
  readonly gender: string;
  readonly ageYears: number | null;
  readonly city: string | null;
}
export interface CheckpointResult {
  readonly outcome:
    | 'registration_captured'
    | 'linked'
    | 'already_registered'
    | 'possible_match'
    | 'candidate_only'
    | 'reconciliation_pending';
  readonly patientId: string | null;
  readonly patientUmr: string | null;
  /** The lead that carries the registration capture (null on link paths
   *  where the appointment had no lead). OPTIONAL in the mirror so older
   *  fixtures stay valid. */
  readonly leadId?: number | null;
  /** THE ONE CAS writer's verdict; null on the zero-write outcomes. A
   *  'failed' stamp means the record resolved but THIS appointment is NOT
   *  linked yet -- the panel must not claim resolution (re-run links,
   *  never re-mints). */
  readonly stampOutcome?: string | null;
  readonly candidates: readonly CheckpointCandidate[];
  /** Capture path: TRUE when the tenant required set is complete on the
   *  lead. OPTIONAL in the mirror (older fixtures). */
  readonly registrationComplete?: boolean;
  /** Capture path: the EXACT missing tenant-required field tokens. */
  readonly missingFields?: readonly string[];
  readonly warnings: readonly string[];
}
export type CheckpointClient = (
  appointmentId: number,
  body: Record<string, unknown>,
) => Promise<CheckpointResult>;

function defaultCheckpointClient(
  appointmentId: number,
  body: Record<string, unknown>,
): Promise<CheckpointResult> {
  return portalApiPost<CheckpointResult>(
    `/appointments/${appointmentId}/registration-checkpoint`,
    body,
  );
}

/** The search-first step's read: THE existing patient-master search (one
 *  search, C13's reuse rule). Injectable for tests. */
export type PatientSearchClient = (q: string) => Promise<PatientSearchResponse>;

function defaultSearchClient(q: string): Promise<PatientSearchResponse> {
  return portalApiGet<PatientSearchResponse>(
    `/patient-master/search?q=${encodeURIComponent(q)}`,
  );
}

/** The registration-config read (INTAKE-V2-BUILD-V1: brief 13.1's resolver
 *  wire). Injectable for tests; failure degrades to the LAW-trio fallback --
 *  a config fault can never block a registration (D3 direction). */
export type RegistrationConfigClient = () => Promise<RegistrationConfigView>;

function defaultConfigClient(): Promise<RegistrationConfigView> {
  return portalApiGet<RegistrationConfigView>('/registration-config');
}

export interface CheckinIdentityStepProps {
  /** The board row being checked in (leadId REQUIRED -- the caller only
   *  opens this panel for an unlinked lead row). */
  readonly row: Pick<
    TodayAppointment,
    'id' | 'leadId' | 'patientName' | 'linkedPatientName' | 'linkedPatientUmr'
  >;
  /** Which hop is held (arrived / checked_in). */
  readonly action: CheckinIdentityAction;
  /** UX gate only (canUseLeadBridge(me)) for the ARRIVED hop's bridge offer;
   *  the CHECKPOINT (checked_in hop) is flag-free -- its backend route reads
   *  no flag (founder ruling 4) and the backend fence is the boundary. */
  readonly bridgeEnabled: boolean;
  /** Fire the held hop (the caller owns the existing mutation + refetch). */
  readonly onProceed: (identityChecked?: boolean) => void;
  /** Close WITHOUT the hop (the row stays exactly as it was). */
  readonly onClose: () => void;
  /** A link/registration succeeded -- the caller refetches the day read so
   *  identity propagates to every surface without a reload (Scope 1e). */
  readonly onLinked: () => void;
  /** Injected for tests; defaults to the real audited reveal client fn. */
  readonly reveal?: (purpose: string) => Promise<RevealOutcomePayload>;
  /** Injected for tests; defaults to the real checkpoint route client. */
  readonly checkpoint?: CheckpointClient;
  /** Injected for tests; defaults to the real patient-master search. */
  readonly search?: PatientSearchClient;
  /** Injected for tests; defaults to the real registration-config read. */
  readonly config?: RegistrationConfigClient;
  /** Injected for tests; defaults to the Supabase auth-session actor. */
  readonly draftActor?: DraftActorResolver;
}

/** Kept exported for compatibility with the pre-wizard shape (the wizard
 *  model's ArrivalWizardForm supersedes it internally). */
export interface CheckpointFormState {
  fullName: string;
  mobile: string;
  ageMode: 'age' | 'dob';
  ageYears: string;
  dob: string;
  gender: string;
  complaint: string;
  alertFlag: boolean;
  consentGiven: boolean;
  guardianName: string;
  guardianRelationship: string;
}

/** DRAFT PRIVACY (PR #414 founder corrective): the draft holds patient
 *  identity data, so it lives in SESSION storage ONLY -- it cannot survive
 *  a browser-session end -- and every read/write is bound to the
 *  AUTHENTICATED ACTOR (below). Device-persistent storage is deliberately
 *  never used for wizard drafts (pinned). Guarded: private mode / SSR
 *  render nothing. */
function readDraftRaw(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}
function writeDraftRaw(key: string, value: string): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(key, value);
  } catch {
    /* a failed draft write loses nothing but resume convenience */
  }
}
function clearDraftRaw(key: string): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** The authenticated-actor resolution the draft is KEYED to (the staff
 *  user's own auth id -- never patient data). No session = NO draft: with
 *  no resolvable actor the wizard neither restores nor writes a draft
 *  (fail-closed for privacy; typing still survives within the mounted
 *  component). A same-tab user switch changes the actor, so every prior
 *  draft parses dead (register-wizard-model.ts parseArrivalDraft). */
export type DraftActorResolver = () => Promise<string | null>;

async function defaultDraftActor(): Promise<string | null> {
  try {
    const mod = await import('@/lib/supabase');
    const client = mod.getSupabaseBrowserClient();
    if (!client) return null;
    const { data } = await client.auth.getSession();
    const id = data.session?.user?.id;
    return typeof id === 'string' && id !== '' ? id : null;
  } catch {
    return null;
  }
}

export default function CheckinIdentityStep({
  row,
  action,
  bridgeEnabled,
  onProceed,
  onClose,
  onLinked,
  reveal,
  checkpoint,
  search,
  config,
  draftActor,
}: CheckinIdentityStepProps): JSX.Element {
  const [linked, setLinked] = useState(false);
  const [identityChecked, setIdentityChecked] = useState(false);
  const [linkedUmr, setLinkedUmr] = useState<string | null>(null);
  const [linkedWarnings, setLinkedWarnings] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<CheckpointResult | null>(null);
  // LEAD-UNTIL-CONSULT ruling: the 'registration_captured' success is a
  // LEAD capture (no patient, no UMR) -- the success copy states the
  // enquiry truth; an INCOMPLETE capture keeps the wizard open and names
  // the exact missing fields.
  const [capturedEnquiry, setCapturedEnquiry] = useState(false);
  const [captureIncomplete, setCaptureIncomplete] = useState<
    readonly string[] | null
  >(null);

  // V-2 REQ-PATIENT-NAME-SINGLE-SOURCE: the LINKED golden-record display name
  // wins on this patient-context check-in drawer; the appointment booking name
  // is the fallback only for an unlinked row.
  const leadName =
    row.linkedPatientName && row.linkedPatientName.trim() !== ''
      ? row.linkedPatientName.trim()
      : row.patientName && row.patientName.trim() !== ''
        ? row.patientName.trim()
        : null;
  const title = leadName ?? 'Enquiry - name pending';
  const leadId = row.leadId ?? null;
  const draftKey = arrivalDraftKey(row.id);

  // The wizard state. The draft (D5, as corrected -- session+actor-bound)
  // restores path/step/form/picked in ONE strict parse AFTER the
  // authenticated actor resolves; a malformed, cross-user, or
  // cross-session draft is discarded whole, never half-used.
  const [path, setPath] = useState<ArrivalPath>('new');
  const [step, setStep] = useState<ArrivalStepId>('search');
  const [form, setForm] = useState<ArrivalWizardForm>(() => ({
    ...EMPTY_ARRIVAL_FORM,
    fullName: leadName ?? '',
  }));
  const [picked, setPicked] = useState<ArrivalPickedPatient | null>(null);
  const [resumed, setResumed] = useState(false);
  /** null = unresolved/none -> the draft seam is fully DISABLED (no read,
   *  no write): a draft exists only when it can be bound to an
   *  authenticated identity (fail-closed for privacy). */
  const [actor, setActor] = useState<string | null>(null);
  const [errors, setErrors] = useState<readonly ArrivalFieldError[]>([]);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [photoSlotOpen, setPhotoSlotOpen] = useState(false);

  // The search step's read state.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly SearchResultCardView[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchUnavailable, setSearchUnavailable] = useState<string | null>(null);
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);

  // The org's registration config (requiredness / chips / consent paths).
  const [orgConfig, setOrgConfig] = useState<RegistrationConfigView>(
    FALLBACK_REGISTRATION_CONFIG,
  );

  const revealFn =
    reveal ??
    ((purpose: string) =>
      revealLeadContact(String(leadId), purpose) as Promise<RevealOutcomePayload>);
  const checkpointFn = checkpoint ?? defaultCheckpointClient;
  const searchFn = search ?? defaultSearchClient;
  const configFn = config ?? defaultConfigClient;

  const isCheckpoint = action === 'checked_in';

  useEffect(() => {
    if (!isCheckpoint) return;
    let cancelled = false;
    configFn()
      .then((c) => {
        if (!cancelled) {
          setOrgConfig(c);
          // REGISTRATION-FIELD-CONTROLS-V1: the contract's State/Country
          // prefill rule ("opens PREFILLED from the config default,
          // editable") -- fills ONLY still-empty fields, so a typed or
          // draft-restored value is never overwritten.
          setForm((prev) => ({ ...prev, ...arrivalAddressDefaultsPatch(prev, c) }));
        }
      })
      .catch(() => {
        /* FALLBACK_REGISTRATION_CONFIG stands -- never blocks registration */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckpoint]);

  // DRAFT PRIVACY: resolve the authenticated actor ONCE, then restore the
  // session-bound draft only when its stored actor matches (a user switch
  // or a fresh browser session yields no restorable draft).
  const draftActorFn = draftActor ?? defaultDraftActor;
  useEffect(() => {
    if (!isCheckpoint || linked) return;
    let cancelled = false;
    void draftActorFn().then((resolved) => {
      if (cancelled || resolved === null || resolved === '') return;
      setActor(resolved);
      const draft = parseArrivalDraft(readDraftRaw(draftKey), row.id, resolved);
      if (draft === null) return;
      setPath(draft.path);
      setStep(draft.step);
      setForm(draft.form);
      setPicked(draft.picked);
      setResumed(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckpoint, draftKey, row.id]);

  // D5: persist the draft on every state change (stronger than the
  // per-Continue law) -- SESSION storage only, and ONLY once an
  // authenticated actor is bound; still zero server writes.
  useEffect(() => {
    if (!isCheckpoint || linked || actor === null) return;
    writeDraftRaw(
      draftKey,
      serializeArrivalDraft({
        version: ARRIVAL_DRAFT_VERSION,
        appointmentId: row.id,
        actor,
        path,
        step,
        form,
        picked,
      }),
    );
  }, [isCheckpoint, linked, actor, draftKey, row.id, path, step, form, picked]);

  // D3: after a failed Continue, focus the first offending field and scroll
  // it into view (runtime-only; SSR renders never run effects).
  const focusPending = useRef(false);
  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    if (typeof document === 'undefined') return;
    const el = document.querySelector<HTMLElement>(
      '.checkin-identity__checkpoint [aria-invalid="true"]',
    );
    if (el !== null) {
      el.focus();
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [errors]);

  const required = useMemo(() => resolvedRequiredFields(orgConfig), [orgConfig]);
  const nowMs = Date.now();
  const isMinor = arrivalFormIsMinor(form, nowMs);

  const patch = (p: Partial<ArrivalWizardForm>): void => {
    setIdentityChecked(false);
    const next = { ...form, ...p };
    setForm(next);
    if (errors.length > 0) setErrors(formErrors(next));
  };

  const runSearch = (): void => {
    const q = query.trim();
    if (q === '' || searching) return;
    setSearching(true);
    setSearchUnavailable(null);
    searchFn(q)
      .then((res) => {
        setResults(
          res.candidates.map((c) => ({
            patientId: c.patientId,
            umr: c.umr,
            fullName: c.fullName,
            gender: c.gender,
            ageYears: c.ageYears,
            city: c.city,
            registeredClinicId: c.registeredClinicId,
            registeredClinicLabel: c.registeredClinicLabel,
          })),
        );
        setSearchedQuery(q);
      })
      .catch(() => {
        // HONEST DEGRADE: the search read is an assist; the binding
        // duplicate check still runs server-first at submit. The register
        // door opens with the honest line instead of a dead end.
        setResults(null);
        setSearchedQuery(q);
        setSearchUnavailable(ARRIVAL_SEARCH_UNAVAILABLE);
      })
      .finally(() => setSearching(false));
  };

  const selectCard = (card: SearchResultCardView): void => {
    // RETURNING path (4 steps): the record is confirmed, never re-created.
    // The searched phone (a phone-mode query) carries into the mobile field
    // so the server's candidacy fence can resolve the link.
    setPicked(card);
    setPath('returning');
    setForm((prev) => ({
      ...prev,
      fullName: card.fullName,
      gender: card.gender === 'unknown' ? '' : card.gender,
      ...(card.ageYears !== null
        ? { ageMode: 'age' as const, ageYears: String(card.ageYears) }
        : {}),
      ...(card.city !== null && card.city !== '' ? { city: card.city } : {}),
      ...(searchedQuery !== null && /^[+\d][\d\s\-()]{5,}$/.test(searchedQuery)
        ? { mobile: searchedQuery }
        : {}),
    }));
    setErrors([]);
    setStep('details');
  };

  const startNewPath = (): void => {
    // The ONLY creation door (C13): after a no-match search or an explicit
    // `None of these`. A phone-shaped query prefills the mobile.
    setPicked(null);
    setPath('new');
    setForm((prev) => ({
      ...prev,
      ...(searchedQuery !== null &&
      /^[+\d][\d\s\-()]{5,}$/.test(searchedQuery) &&
      prev.mobile.trim() === ''
        ? { mobile: searchedQuery }
        : {}),
    }));
    setErrors([]);
    setStep('details');
  };

  const formErrors = (values = form): readonly ArrivalFieldError[] => [
    ...(path === 'returning' ? validateArrivalReturningConfirm(values) : validateArrivalDetails(values, required)),
    ...validateArrivalReason(values, required),
    ...(path === 'new' ? validateArrivalConsent(values, arrivalFormIsMinor(values, nowMs)) : []),
  ];

  const discardRegistration = (): void => {
    clearDraftRaw(draftKey);
    setConfirmCancel(false);
    onClose();
  };

  const submitCheckpoint = (choice?: {
    action: 'link' | 'create_new';
    patientId?: string;
  }): void => {
    if (busy) return;
    const found = formErrors();
    setErrors(found);
    if (found.length > 0) { focusPending.current = true; return; }

    setBusy(true);
    setError(null);
    // RETURNING path: the explicit pick rides the ONE submit as the
    // patientChoice the server re-validates against ITS OWN surfaced
    // candidates (the #402/#406/#407 candidacy fence -- a pick the server
    // never surfaced is refused, never linked).
    const effectiveChoice =
      choice ??
      (path === 'returning' && picked !== null
        ? { action: 'link' as const, patientId: picked.patientId }
        : undefined);
    const body = buildArrivalCheckpointBody(form, Date.now(), effectiveChoice);
    void checkpointFn(row.id, body)
      .then((result) => {
        // LEAD-UNTIL-CONSULT ruling (DOCTOR-OS-EXPERIENCE-V1, 2026-08-04):
        // 'registration_captured' = the field set landed ON THE LEAD --
        // no patient, no UMR, no stamp. Complete -> the success state with
        // the enquiry truth; incomplete -> the wizard STAYS OPEN naming
        // the exact missing fields (completion continues, draft kept).
        if (result.outcome === 'registration_captured') {
          setVerdict(null);
          if (result.registrationComplete === false) {
            setCaptureIncomplete(result.missingFields ?? []);
            onLinked(); // the capture landed -- board truth propagates
            return;
          }
          setCaptureIncomplete(null);
          setCapturedEnquiry(true);
          setLinked(true);
          setLinkedUmr(null);
          setLinkedWarnings(result.warnings);
          clearDraftRaw(draftKey);
          onLinked();
          return;
        }
        if (
          result.outcome === 'linked' ||
          result.outcome === 'already_registered'
        ) {
          if (result.registrationComplete === false) {
            setCaptureIncomplete(result.missingFields ?? []);
            setVerdict(null);
            return;
          }
          if (result.stampOutcome === 'failed') {
            // HONEST STATE: the record resolved but the CAS stamp did NOT
            // land -- THIS appointment is still unlinked, so the panel must
            // not claim resolution. Stay on the form and surface the
            // server's own warning; re-running links, never re-mints.
            setVerdict(null);
            setError(
              result.warnings.length > 0
                ? result.warnings.join(' ')
                : 'The appointment link could not be written. Re-run the checkpoint - it will link, never re-mint.',
            );
            return;
          }
          setVerdict(null);
          setCaptureIncomplete(null);
          setLinked(true);
          setLinkedUmr(result.patientUmr);
          setLinkedWarnings(result.warnings);
          clearDraftRaw(draftKey);
          onLinked();
          return;
        }
        // A DERIVED state: candidates render with NO default selection --
        // the staff picks, or registers new where that stays lawful.
        setVerdict(result);
      })
      .catch((err: unknown) => {
        // ERROR-PASSTHROUGH: the server's own message renders verbatim
        // (the 409s carry the honest state -- pre-apply fail-closed,
        // identity conflict, checkpoint-anchor refusal).
        setError(
          err instanceof PortalApiError || err instanceof Error
            ? /^HTTP \d{3}$/.test(err.message)
              ? 'Registration save was not confirmed. Your entries are kept. Retry to check the saved result.'
              : err.message
            : 'Registration failed - try again.',
        );
      })
      .finally(() => setBusy(false));
  };

  const footErrors = errors;
  const checkpointError = error !== null ? <p className="checkin-identity__error ac-banner ac-banner--danger"
    role="alert" data-testid="checkpoint-error">{error}</p> : null;

  return (
    <ModalShell workspace
      eyebrow={isCheckpoint ? 'Your next action' : CHECKIN_IDENTITY_EYEBROW}
      title={isCheckpoint ? 'Patient registration' : title}
      onClose={() => { if (!busy) onClose(); }}
      closeDisabled={busy}
      trapFocus
      testId="checkin-identity-step"
      footer={
        <div className="checkin-identity__actions">
          {linked && isCheckpoint ? <label><Checkbox checked={identityChecked} onCheckedChange={checked=>setIdentityChecked(checked)}/> Identity checked</label> : null}
      {linked ? (
            <AcButton
              size="sm"
              variant="primary"
              disabled={isCheckpoint && !identityChecked}
              onClick={() => onProceed(identityChecked)}
              data-testid="checkin-identity-proceed"
            >
              {PROCEED_NOW_COPY[action]}
            </AcButton>
          ) : action === 'arrived' ? (
            // THE ONE SURVIVING SKIP DOOR (ruling 3): arrival is one-tap and
            // identity-free -- physical presence is never blocked. The
            // checked_in hop renders NO skip door: the checkpoint is the way
            // through (server-enforced either way).
            <AcButton
              size="sm"
              variant="secondary"
              onClick={() => onProceed(false)}
              data-testid="checkin-identity-skip"
            >
              {PROCEED_SKIP_COPY.arrived}
            </AcButton>
          ) : null}
          <AcButton
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="checkin-identity-not-now"
          >
            {NOT_NOW_COPY}
          </AcButton>
        </div>
      }
    >
      {!isCheckpoint ? (
        <p className="checkin-identity__intro">{CHECKIN_IDENTITY_INTRO}</p>
      ) : null}

      {isCheckpoint ? <section aria-label="Selected patient and appointment" className="checkin-identity__context">
        <h3>{title}</h3>
        {row.linkedPatientUmr ? <p>UMR {row.linkedPatientUmr}</p> : null}
        <p>Appointment #{row.id}</p>
      </section> : null}

      {leadId !== null && (!isCheckpoint || (!linked && step === 'search' && verdict === null)) ? (
        <div className="checkin-identity__reveal">
          <p className="checkin-identity__hint">{CHECKIN_IDENTITY_PHONE_HINT}</p>
          <LeadContactRevealControl
            availability="unknown"
            reveal={revealFn}
            initialPurpose="front_desk_checkin"
            allowAttemptWhenUnknown
          />
        </div>
      ) : null}

      {linked ? (
        // A6: what happened, the ID, the three named stages with their
        // authors, and the pending consent photo riding along (C14). The
        // primary next act stays the footer's held hop (`Check in now`);
        // the intake handover (stage 2) is the board's existing Start-intake
        // door -- V2 mints no new door (T10).
        <div className="ac-wizard__success" data-testid="arrival-success">
          <span className="ac-wizard__success-tick" aria-hidden="true" />
          <h3 className="ac-wizard__success-headline">{ARRIVAL_SUCCESS_HEADLINE}</h3>
          {capturedEnquiry ? (
            // LEAD-UNTIL-CONSULT ruling: no patient, no UMR minted here --
            // the person stays an enquiry until the doctor starts the
            // consult (the honest capture truth, stated plainly).
            <p
              className="ac-wizard__success-note"
              data-testid="arrival-captured-note"
            >
              {capturedSuccessLine(form.fullName || title)}
            </p>
          ) : null}
          {linkedUmr !== null ? (
            <div className="ac-wizard__id-strip" data-testid="arrival-success-id">
              <span className="ac-review__key">{ARRIVAL_SUCCESS_ID_LABEL}</span>
              <span className="ac-review__value ac-record-card__num">{linkedUmr}</span>
            </div>
          ) : null}
          <ThreeStageStrip
            stage1={{ title: ARRIVAL_STAGE_1_TITLE, author: ARRIVAL_STAGE_1_AUTHOR }}
            stage2={{ title: ARRIVAL_STAGE_2_TITLE, author: ARRIVAL_STAGE_2_AUTHOR }}
            stage3={{ title: ARRIVAL_STAGE_3_TITLE, author: ARRIVAL_STAGE_3_AUTHOR }}
          />
          {form.consentPath === 'paper' ? (
            photoSlotOpen ? (
              <ConsentPhotoCaptureSlot
                seam={{ subject: 'registration_consent' }}
                onClose={() => setPhotoSlotOpen(false)}
              />
            ) : (
              <PendingPhotoBlock
                line={PENDING_PHOTO_LINE_SUCCESS}
                onTakePhoto={() => setPhotoSlotOpen(true)}
                testId="arrival-pending-photo-success"
              />
            )
          ) : null}
        </div>
      ) : null}
      {linked && linkedWarnings.length > 0 ? (
        // Server warnings survive success (e.g. a merge-queue routing note):
        // rendered verbatim, never swallowed.
        <ul
          className="checkin-identity__warnings"
          data-testid="checkpoint-warnings"
        >
          {linkedWarnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}

      {!linked && isCheckpoint ? (
        // THE CHECKPOINT (checked_in hop): flag-free by ruling 4 -- the
        // appointment-anchored route reads no flag, so this wizard renders
        // for every caller with board access; the backend fence is the
        // boundary.
        <div className="checkin-identity__checkpoint ac-wizard" data-testid="checkpoint-form">
          {(step === 'search' || verdict !== null) ? checkpointError : null}
          {captureIncomplete !== null ? (
            // LEAD-UNTIL-CONSULT ruling: the capture LANDED on the lead but
            // the tenant required set is incomplete -- the wizard stays
            // open naming the exact missing fields so completion continues
            // (the doctor's Start Consult stays blocked until it does).
            <p
              className="checkin-identity__hint ac-banner ac-banner--warn"
              role="alert"
              data-testid="checkpoint-capture-incomplete"
            >
              {captureIncompleteLine(captureIncomplete)}
            </p>
          ) : null}

          {verdict !== null ? (
            <div
              className="checkin-identity__candidates ac-wizard__panel"
              data-testid="checkpoint-candidates"
            >
              {verdict.outcome === 'reconciliation_pending' ? (
                <p
                  className="checkin-identity__hint ac-banner ac-banner--warn"
                  role="alert"
                  data-testid="checkpoint-reconciliation"
                >
                  {CHECKPOINT_RECONCILIATION_COPY}
                </p>
              ) : (
                <p className="checkin-identity__hint ac-banner ac-banner--info">
                  Possible existing records - pick the right one, or register
                  new. Nothing is selected for you.
                </p>
              )}
              <ul className="ac-wizard__cards checkin-identity__candidate-list">
                {verdict.candidates.map((c) => (
                  <li key={c.patientId} className="ac-record-card" data-testid="checkpoint-candidate">
                    <dl className="ac-record-card__facts">
                      <div className="ac-record-card__fact">
                        <dt>Name</dt>
                        <dd>{c.fullName}</dd>
                      </div>
                      <div className="ac-record-card__fact">
                        <dt>ID</dt>
                        <dd className="ac-record-card__num">{c.umr}</dd>
                      </div>
                      {c.ageYears !== null ? (
                        <div className="ac-record-card__fact">
                          <dt>Age</dt>
                          <dd className="ac-record-card__num">{c.ageYears}</dd>
                        </div>
                      ) : null}
                      {c.city !== null ? (
                        <div className="ac-record-card__fact">
                          <dt>City</dt>
                          <dd>{c.city}</dd>
                        </div>
                      ) : null}
                    </dl>
                    <AcButton
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        submitCheckpoint({ action: 'link', patientId: c.patientId })
                      }
                      data-testid="checkpoint-link-candidate"
                    >
                      {CHECKPOINT_LINK_CANDIDATE_LABEL}
                    </AcButton>
                  </li>
                ))}
              </ul>
              {verdict.outcome !== 'reconciliation_pending' ? (
                // The reconciliation state hides the register-new door:
                // identity is DISPUTED, so a fresh capture cannot proceed
                // choicelessly (LAW 2 -- server-enforced either way). The
                // old writer-refused-duplicate branch retired with the
                // mint: LEAD-UNTIL-CONSULT ruling (DOCTOR-OS-EXPERIENCE-V1,
                // 2026-08-04).
                <AcButton
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => submitCheckpoint({ action: 'create_new' })}
                  data-testid="checkpoint-create-anyway"
                >
                  {CHECKPOINT_CREATE_ANYWAY_LABEL}
                </AcButton>
              ) : null}
              <AcButton
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setVerdict(null)}
                data-testid="checkpoint-back-edit"
              >
                {CHECKPOINT_BACK_EDIT_LABEL}
              </AcButton>
            </div>
          ) : (
            <div className="checkin-identity__fields ac-wizard__body">
              {resumed && step !== 'search' ? (
                // S4: the resumed state names itself -- and names the truth
                // that the draft wrote nothing anywhere (no half-made
                // patient exists).
                <div className="ac-banner ac-banner--good" data-testid="arrival-resumed">
                  <strong>{ARRIVAL_RESUMED_HEAD}</strong> {ARRIVAL_RESUMED_SUB}
                </div>
              ) : null}

              <div hidden={step !== 'search'}>
                <RegisterSearchStep
                  query={query}
                  onQueryChange={setQuery}
                  onSearch={runSearch}
                  results={results}
                  searching={searching}
                  searchUnavailable={searchUnavailable}
                  onSelect={selectCard}
                  onRegisterNew={startNewPath}
                  busy={busy}
                />
              </div>

              <div hidden={step === 'search'}>
                {path === 'returning' && picked !== null ? (
                  <>
                    <RegisterReturningConfirm
                      picked={picked}
                      mobile={form.mobile}
                      onMobileChange={(v) => patch({ mobile: v })}
                      errors={footErrors}
                      busy={busy}
                    />
                    <button
                      type="button"
                      className="ac-wizard__quiet"
                      disabled={busy}
                      onClick={() => {
                        // `Something changed` -> the full editable details.
                        setPath('new');
                        setPicked(null);
                      }}
                      data-testid="arrival-edit-details"
                    >
                      {ARRIVAL_EDIT_DETAILS}
                    </button>
                  </>
                ) : (
                  <RegisterDetailsFields
                    choicePresentation="select"
                    form={form}
                    onChange={patch}
                    required={required}
                    handlingCategories={orgConfig.handlingCategories}
                    /* REGISTRATION-FIELD-CONTROLS-V1: the governed picklist
                       vocabularies (title / relation / relation-title /
                       occupation + district cascade) -- the SAME choices the
                       desk modal renders, derived once from the org config. */
                    sectionsPrefill={registrationSectionsPrefill(orgConfig)}
                    errors={footErrors}
                    busy={busy}
                    testIds={{
                      name: 'checkpoint-name',
                      mobile: 'checkpoint-mobile',
                      ageToggle: 'checkpoint-age-toggle',
                      age: 'checkpoint-age',
                      dob: 'checkpoint-dob',
                      gender: 'checkpoint-gender',
                      city: 'arrival-city',
                    }}
                  />
                )}
              </div>

              <div hidden={step === 'search'}>
                <RegisterReasonStep
                  form={form}
                  onChange={patch}
                  chips={orgConfig.reasonChips}
                  reasonRequired={required.has('reason_for_visit')}
                  errors={footErrors}
                  busy={busy}
                  complaintTestId="checkpoint-complaint"
                  alertTestId="checkpoint-alert"
                />
              </div>

              {path === 'new' ? (
                <div hidden={step === 'search'}>
                  {photoSlotOpen ? (
                    <ConsentPhotoCaptureSlot
                      seam={{ subject: 'registration_consent' }}
                      onClose={() => setPhotoSlotOpen(false)}
                    />
                  ) : null}
                  <RegisterConsentStep
                    form={form}
                    onChange={patch}
                    consentPaths={orgConfig.consentPaths}
                    isMinor={isMinor}
                    errors={footErrors}
                    busy={busy}
                    onTakePhoto={() => setPhotoSlotOpen(true)}
                    consentTestId="checkpoint-consent"
                    guardianNameTestId="checkpoint-guardian-name"
                    guardianRelationshipTestId="checkpoint-guardian-relationship"
                  />
                </div>
              ) : null}

              {step !== 'search' ? <>
                {verdict === null ? checkpointError : null}
                <p className="ac-wizard__foot-note" data-testid="checkpoint-clinic-note">{CHECKPOINT_CLINIC_NOTE}</p>
                <StillNeededLine line={stillNeededLine(footErrors)} />
                <div className="ac-wizard__foot">
                  <button type="button" className="ac-wizard__quiet ac-wizard__quiet--danger"
                    disabled={busy} onClick={() => setConfirmCancel(true)} data-testid="arrival-cancel-registration">
                    {ARRIVAL_CANCEL_REGISTRATION}
                  </button>
                  <span className="ac-wizard__foot-spacer" />
                  <AcButton variant="primary" disabled={busy} onClick={() => submitCheckpoint()} data-testid="checkpoint-submit">
                    {busy ? 'Saving...' : ARRIVAL_SUBMIT_LABEL}
                  </AcButton>
                </div>
              </> : null}
            </div>
          )}
          {confirmCancel ? (
            // S6: the dangerous action names exactly what is lost and what
            // is not; the destructive act needs its own confirm.
            <ConfirmDialog
              title={ARRIVAL_CANCEL_DIALOG_TITLE}
              confirmLabel={ARRIVAL_CANCEL_DIALOG_DISCARD}
              cancelLabel={ARRIVAL_CANCEL_DIALOG_KEEP}
              destructive
              onConfirm={discardRegistration}
              onCancel={() => setConfirmCancel(false)}
              confirmTestId="arrival-discard-confirm"
              testId="arrival-cancel-dialog"
            >
              <p>{arrivalCancelDialogBody(form.fullName || title)}</p>
            </ConfirmDialog>
          ) : null}
        </div>
      ) : null}

      {!linked && !isCheckpoint ? (
        bridgeEnabled && leadId !== null ? (
          <ConvertLeadToPatientControl
            leadId={leadId}
            bridgeEnabled
            leadName={leadName}
            onConverted={() => {
              setLinked(true);
              onLinked();
            }}
          />
        ) : !bridgeEnabled ? (
          // GATE HONESTY (Scope 1f), ARRIVED hop only: one line, no dead
          // button, no flag flip. The checked_in hop's checkpoint above is
          // flag-free and never renders this.
          <p
            className="checkin-identity__gate-dark"
            data-testid="checkin-identity-gate-dark"
          >
            {CHECKIN_IDENTITY_GATE_DARK_COPY}
          </p>
        ) : null /* leadId null: defensive -- the caller only opens for lead rows */
      ) : null}
    </ModalShell>
  );
}
